import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, FileText, GripVertical, Loader2, Info, Copy, Search, Link2 } from "lucide-react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { saveClaim } from "@/lib/noteClaim";
import { api } from "@/lib/api";
import { useClient } from "@/lib/clientContext";
import { MODIFIER_CATALOG, MODIFIER_NAME } from "./modifierCatalog";
import SourceEvidence from "./SourceEvidence";
import InsurancePanel from "./InsurancePanel";
import EncounterNote from "./EncounterNote";
import { useChartTabs } from "@/lib/chartTabs";
import { useCw } from "./cwContext";
import RailHandle, { useRailWidth } from "./RailHandle";
import { useProviders, titleCase } from "@/components/practice-setup/psPrimitives";
import { useProviderRules } from "@/lib/codingRules";
import { CodingRulesVerdict, CodingRulesProviderPanel, CodeRulesInspectorBlock, RulesChangedSheet } from "./ClaimCodingRules";
import ClaimLevelBoxes from "./ClaimLevelBoxes";
import { AddCptLine, CptPicker } from "./AddCptLine";
import "./claimform-v20.css";

/*
  Single-view claim form. One editable claim SNAPSHOT (note.claim) is the source of truth;
  every field commits to local state on change and the whole claim persists in ONE call via
  POST /notes/{id}/claim (saveClaim). Verified against the live API:
    - modifiers, line dx pointers, providers, facility, payer, prior_auth all persist on save
    - a typed diagnosis rides claim.diagnoses (not icd_codes); the engine does not overwrite it
    - Box 21 reorder is done in the snapshot (pointers reference codes, so they follow)
  NOT supported (so NOT built): per-code confidence figure (no field), fee-schedule charge
  (billed_charge is null, no source), submit/837/print/resend, suggestion-accept, rules API.
  The emblem marks revQ's own output only: "revQ's verdict" and "What revQ noticed".
*/

const INK = "#141412";
// Display-only title case for ALL-CAPS names; credentials (M.D., MD, DO, NP, PhD…) stay as written.
const CRED = /^(m\.?d\.?|d\.?o\.?|n\.?p\.?|p\.?a\.?(-c)?|ph\.?d\.?|r\.?n\.?|dnp|fnp(-c)?|aprn|lcsw|dpm|dds|od|pc|p\.c\.|llc|inc\.?)[,.]?$/i;
const nameCase = (s) => (!s || s !== String(s).toUpperCase() ? s : String(s).split(" ").map((w) => (CRED.test(w) ? w : w.toLowerCase().replace(/(^|[-'(])([a-z])/g, (m, a, b) => a + b.toUpperCase()))).join(" "));
const longDate = (d) => { if (!d) return "—"; const t = new Date(`${String(d).slice(0, 10)}T12:00:00`); return isNaN(t) ? String(d) : t.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); };
const INDIGO = "#5856D6";
const money = (v) => (v == null || v === "" || isNaN(Number(v)) ? null : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const Emblem = ({ size = 14 }) => <img src="/revQ_emblem_black.webp" alt="revQ" style={{ height: size, width: "auto", objectFit: "contain", opacity: 0.9 }} />;
const dxTyped = (d) => d.source === "typed" || d.provenance === "user" || d.provenance === "typed";
const dxInferred = (d) => !dxTyped(d) && d.provenance && !["clear", "documented"].includes(String(d.provenance));
const dxAdded = (d) => d.source === "added";
const normCode = (s) => String(s || "").toUpperCase().replace(/\./g, "");
const round2 = (n) => Math.round(Number(n) * 100) / 100;
// Box 24G: billed_charge.units → units → 1 (a CPT line is 1 unit by default).
const lineUnits = (l) => { const u = l?.billed_charge?.units ?? l?.units; return u == null || u === "" ? 1 : Number(u); };
// Box 24F: billed total → unit × units → corpus avg paid (est) → none.
const lineCharge = (l) => {
  const bc = l?.billed_charge || {};
  if (bc.total != null && bc.total !== "") return { amt: Number(bc.total), est: false };
  if (bc.unit != null && bc.unit !== "") return { amt: Number(bc.unit) * (Number(bc.units) || 1), est: false };
  if (l?.avg_paid?.amt != null) return { amt: Number(l.avg_paid.amt), est: true };
  return null;
};

function composeClaim(note) {
  if (note?.claim && typeof note.claim === "object") {
    // Saved snapshot is the truth; carry corpus avg over from the deduced lines for reference only.
    const c = JSON.parse(JSON.stringify(note.claim));
    const avgBy = new Map((note.cpt_codes || []).map((x) => [String(x.code), x.avg_paid]));
    (c.service_lines || []).forEach((l) => { if (!l.avg_paid && avgBy.get(String(l.code))) l.avg_paid = avgBy.get(String(l.code)); });
    return c;
  }
  const prov = { name: note?.provider_name || null, npi: note?.npi_number || null, taxonomy_code: note?.taxonomy_code || null, taxonomy_desc: note?.taxonomy_desc || null };
  return {
    rendering_provider: { ...prov }, provider: { ...prov },
    billing_provider: { name: null, npi: null, tax_id: null, address: null },
    referring_provider: { name: null, npi: null },
    facility: { pos_code: note?.pos_code || note?.place_of_service?.code || null, pos_desc: note?.place_of_service?.description || null, npi: null, name: null, address: null },
    payer: { payer_name: note?.payer_name || null, payer_id: note?.payer_id || null, claim_frequency: "1" },
    diagnoses: (note?.icd_codes || []).map((d) => ({ ...d })),
    service_lines: (note?.cpt_codes || []).map((c) => ({
      ...c,
      code: c.code, description: c.description || "", dos_from: note?.date_of_service || null, dos_to: note?.date_of_service || null,
      modifiers: Array.isArray(c.modifiers) ? [...c.modifiers] : [], dx: (c.dx || []).map((x) => ({ ...x })),
      avg_paid: c.avg_paid || null, evidence: c.evidence || "", rationale: c.rationale || "",
    })),
    claim_details: {}, date_of_service: note?.date_of_service || null, prior_auth_number: null,
  };
}

export default function ClaimFormV20({ note: noteProp, onScanNext, onClose, onChanged }) {
  const [note, setNote] = useState(noteProp);
  const [claim, setClaim] = useState(() => composeClaim(noteProp));
  const [dirty, setDirty] = useState(false);
  const [reordered, setReordered] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sel, setSel] = useState({ type: "verdict" });
  const [pop, setPop] = useState(null); // { kind:'mod', i } modifier popover
  const [ptrMenu, setPtrMenu] = useState(null); // line index whose "+" pointer menu is open
  const [touchedLines, setTouchedLines] = useState(() => new Set()); // lines a human has edited (provenance)
  const [showSource, setShowSource] = useState(false);
  const [insOpen, setInsOpen] = useState(false); // eslint-disable-line no-unused-vars
  const [whyOpen, setWhyOpen] = useState({});
  const cw = useCw();
  // Claim tabs carry their own starting panel; the overlay keeps the shared sessionStorage value.
  const [railTab, setRailTab] = useState(() => (cw?.tabKey ? (cw.initialRail === "note" ? "note" : "verdict") : sessionStorage.getItem("revq.cfv.railTab") === "note" ? "note" : "verdict"));
  const [noteJump, setNoteJump] = useState(null); // { text, nonce } — a Why?/blocker jump into the encounter note
  const [boxesOpen, setBoxesOpen] = useState(false);
  const [noteEditing, setNoteEditing] = useState(false);
  const { openChart } = useChartTabs();
  useEffect(() => { cw?.setEditing?.(noteEditing); }, [noteEditing]); // eslint-disable-line react-hooks/exhaustive-deps
  const rail = useRailWidth(noteEditing);
  const clbRef = useRef(null);
  const dragIdx = useRef(null);
  const { selectedClientId } = useClient();
  const { data: allProviders = [] } = useProviders();
  const clientId = noteProp?.client_id || selectedClientId;
  // Rendering providers come from the note-to-claim registry, scoped to THIS claim's client.
  const providers = useMemo(
    () => (clientId ? allProviders.filter((p) => String(p.client_id ?? p.client_4digit_id ?? "") === String(clientId)) : allProviders),
    [allProviders, clientId]
  );
  const [payers, setPayers] = useState([]);
  const [billingProviders, setBillingProviders] = useState([]);
  const [serviceLocations, setServiceLocations] = useState([]);
  const [referringProviders, setReferringProviders] = useState([]);
  useEffect(() => {
    const params = clientId ? { client_id: clientId } : {};
    api.get("/api/edi/note-to-claim/payers", { params }).then((r) => setPayers(r.data?.payers || [])).catch(() => setPayers([]));
    api.get("/api/revq/billing-providers", { params }).then((r) => setBillingProviders(r.data?.billing_providers || [])).catch(() => setBillingProviders([]));
    api.get("/api/revq/service-locations", { params }).then((r) => setServiceLocations(r.data?.service_locations || [])).catch(() => setServiceLocations([]));
    api.get("/api/revq/referring-providers", { params }).then((r) => setReferringProviders(r.data?.providers || r.data?.referring_providers || [])).catch(() => setReferringProviders([]));
  }, [clientId]);

  useEffect(() => { setNote(noteProp); setClaim(composeClaim(noteProp)); setDirty(false); setReordered(false); setTouchedLines(new Set()); setSel({ type: "verdict" }); setNoteJump(null); setBoxesOpen(false); }, [noteProp]);
  useEffect(() => {
    if (!dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const mutate = (fn) => { setClaim((c) => { const n = JSON.parse(JSON.stringify(c)); fn(n); return n; }); setDirty(true); };

  const dx = claim.diagnoses || [];
  const lines = claim.service_lines || [];
  // dx_scope "unlinked" = revQ genuinely couldn't map a diagnosis to this CPT under the corpus.
  const scopeOf = (i) => note?.cpt_codes?.[i]?.dx_scope;
  const isUnlinked = (i) => !((lines[i]?.dx) || []).length && scopeOf(i) === "unlinked";
  const pointedElsewhere = (code, exclI) => lines.reduce((acc, l, k) => acc + (k !== exclI && (l.dx || []).some((x) => x.code === code) ? 1 : 0), 0);
  // Menu ranks by revQ's own cross-line pattern on THIS claim — not a per-line clinical recommendation.
  const menuRanked = (i) => {
    const onLine = new Set(((lines[i]?.dx) || []).map((x) => x.code));
    const avail = dx.filter((d) => !onLine.has(d.code)).map((d) => ({ ...d, n: numOf(d.code), cnt: pointedElsewhere(d.code, i) }));
    return { elsewhere: avail.filter((d) => d.cnt > 0).sort((a, b) => b.cnt - a.cnt), also: avail.filter((d) => d.cnt === 0) };
  };
  const numOf = (code) => { const i = dx.findIndex((d) => d.code === code); return i >= 0 ? i + 1 : null; };
  const usedCodes = useMemo(() => new Set(lines.flatMap((l) => (l.dx || []).map((x) => x.code))), [lines]);
  const linesForDx = (code) => lines.filter((l) => (l.dx || []).some((x) => x.code === code)).length;

  const dxAddedCount = dx.filter(dxAdded).length;
  const dxNoteCount = dx.length - dxAddedCount;
  const addDx = (node) => {
    const codeD = node.code_dotted || node.code;
    if (!codeD || dx.some((d) => normCode(d.code) === normCode(codeD))) return;
    const n = dx.length + 1;
    mutate((c) => c.diagnoses.push({ code: codeD, description: node.description || "", source: "added", provenance: "added", added_fy: node.fy || null }));
    setSel({ type: "dx", code: codeD });
    toast.success(`${codeD} added to Box 21 as diagnosis ${n} — point a line at it`);
  };

  const patient = note?.patient_name || "";
  const navigate = useNavigate();
  const [leavingToChart, setLeavingToChart] = useState(false);
  // Matched mirrors the Create Claims listing: a roster note counts as matched only when the
  // API actually resolved it (match_status "matched" or "preselected", or a real MRN is present).
  // match_source === "roster" alone is NOT enough — "not_matched"/"insufficient" roster notes
  // carry no MRN and must read as not matched, same as the listing.
  const patientMatched =
    note?.match_status === "matched" ||
    note?.match_status === "preselected" ||
    !!note?.mrn;
  const payerName = claim.payer?.payer_name || "";

  // Encounter note (dictation-only, read-only) — everything comes straight from note.encounter.
  const enc = note?.encounter || null;
  const hasEncounter = !!(enc && (enc.soap_note || enc.soap_text || enc.formatted_note || enc.transcript));
  const encSectionCount = useMemo(() => {
    if (!hasEncounter) return 0;
    let n = 1; // the note body
    if (Array.isArray(enc.prescriptions) && enc.prescriptions.length) n++;
    if (Array.isArray(enc.represcribe_meds) && enc.represcribe_meds.length) n++;
    if (Array.isArray(enc.patient_instructions?.sections) && enc.patient_instructions.sections.length) n++;
    if (Array.isArray(enc.patient_comms?.messages) && enc.patient_comms.messages.length) n++;
    if (String(enc.transcript || "").trim()) n++;
    return n;
  }, [enc, hasEncounter]);
  useEffect(() => { if (!cw?.tabKey) sessionStorage.setItem("revq.cfv.railTab", railTab); }, [railTab]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!hasEncounter && railTab === "note") setRailTab("verdict"); }, [hasEncounter]);
  const openInChart = async () => {
    const t = toast.loading("Opening chart…");
    try {
      const { data } = await api.get("/api/edi/note-to-claim/pg-patient-search", { params: { client_id: note?.client_id, q: note?.mrn || note?.patient_name } });
      const results = Array.isArray(data?.results) ? data.results : [];
      const match = (note?.mrn && results.find((r) => String(r.mrn) === String(note.mrn))) || results[0];
      const uuid = match?.chart_uuid || match?.chart_id;
      toast.dismiss(t);
      if (!uuid) { toast.error("No linked chart found for this patient."); return; }
      // Inside a claim tab: open the chart tab right after it; its back link returns to this claim.
      if (cw?.tabKey && openChart) { openChart({ uuid, name: note?.patient_name || "Patient", after: cw.tabKey, fromLabel: "Back to claim" }); return; }
      // Open in the SAME tab, carrying a return target so the chart can route back to
      // this exact claim (note id + client) in the Claims Queue workspace.
      const ret = `/create-claims?claim=${encodeURIComponent(note?.id || "")}&client=${encodeURIComponent(note?.client_id || "")}`;
      setLeavingToChart(true);
      setTimeout(() => navigate(`/chart-v2?uuid=${encodeURIComponent(uuid)}&return=${encodeURIComponent(ret)}`), 180);
    } catch { toast.dismiss(t); toast.error("Could not open the chart."); }
  };
  const jumpToNote = (text) => { if (!hasEncounter) return; setRailTab("note"); setNoteJump({ text: text || "", nonce: Date.now() }); };

  const providerOpts = useMemo(() => providers.map((p) => ({ value: String(p.npi_number), label: `${p.provider_name || "—"} · NPI ${p.npi_number}`, raw: p })), [providers]);
  const payerOpts = useMemo(() => payers.map((x) => ({ value: String(x.id), label: `${x.name} · ${x.id}`, raw: x })), [payers]);

  // Defensive field accessors — the /api/revq list rows are untyped in the spec.
  const bpName = (b) => b.name || b.organization_name || b.org_name || b.provider_name || b.billing_name || "—";
  const bpNpi = (b) => b.npi || b.npi_number || b.billing_npi || "";
  const bpTax = (b) => b.tax_id || b.ein || b.tin || b.tax_id_number || "";
  const slName = (s) => s.name || s.location_name || s.facility_name || "—";
  const slPos = (s) => s.pos_code || s.place_of_service || s.pos || "";
  const slPosDesc = (s) => s.pos_desc || s.pos_description || s.description || "";
  const slNpi = (s) => s.npi || s.npi_number || s.facility_npi || "";
  const rpName = (r) => r.name || r.provider_name || r.full_name || "—";
  const rpNpi = (r) => r.npi || r.npi_number || "";
  const rpSpec = (r) => r.specialty || r.taxonomy_desc || r.provider_type || "";

  const billingOpts = useMemo(() => billingProviders.map((b) => ({ value: String(bpNpi(b) || bpName(b)), label: `${bpName(b)}${bpNpi(b) ? ` · NPI ${bpNpi(b)}` : ""}`, raw: b })), [billingProviders]);
  const facilityOpts = useMemo(() => serviceLocations.map((s) => ({ value: String(slNpi(s) || slName(s)), label: `${slName(s)}${slPos(s) ? ` · POS ${slPos(s)}` : ""}`, raw: s })), [serviceLocations]);
  const referringOpts = useMemo(() => referringProviders.map((r) => ({ value: String(rpNpi(r) || rpName(r)), label: `${rpName(r)}${rpNpi(r) ? ` · NPI ${rpNpi(r)}` : ""}${rpSpec(r) ? ` · ${rpSpec(r)}` : ""}`, raw: r })), [referringProviders]);

  const pickProvider = (npi) => {
    const p = providers.find((x) => String(x.npi_number) === String(npi));
    if (!p) return;
    const blk = { name: p.provider_name || null, npi: p.npi_number || null, taxonomy_code: p.taxonomy_code || null, taxonomy_desc: p.taxonomy_desc || p.provider_type || null };
    mutate((c) => { c.rendering_provider = { ...blk }; c.provider = { ...blk }; });
  };
  const pickBilling = (val) => { const b = billingProviders.find((x) => String(bpNpi(x) || bpName(x)) === String(val)); if (!b) return; mutate((c) => { c.billing_provider = { name: bpName(b), npi: bpNpi(b) || null, tax_id: bpTax(b) || null, address: b.address || null }; }); };
  const pickFacility = (val) => { const s = serviceLocations.find((x) => String(slNpi(x) || slName(x)) === String(val)); if (!s) return; mutate((c) => { c.facility = { ...(c.facility || {}), name: slName(s), pos_code: slPos(s) || c.facility?.pos_code || null, pos_desc: slPosDesc(s) || null, npi: slNpi(s) || null, address: s.address || null }; }); };
  const pickReferring = (val) => { const r = referringProviders.find((x) => String(rpNpi(x) || rpName(x)) === String(val)); if (!r) return; mutate((c) => { c.referring_provider = { name: rpName(r), npi: rpNpi(r) || null, specialty: rpSpec(r) || null, address: r.address || null }; }); };
  const clearReferring = () => mutate((c) => { c.referring_provider = { name: "", npi: "", specialty: "", address: null }; });
  const pickPayer = (id) => { const x = payers.find((p) => String(p.id) === String(id)); if (!x) return; mutate((c) => { c.payer = { ...(c.payer || {}), payer_name: x.name, payer_id: x.id }; }); };

  const observations = useMemo(() => {
    const out = [];
    (note?.cpt_codes || []).forEach((c) => (Array.isArray(c.suggestions) ? c.suggestions : []).forEach((s) => {
      const text = typeof s === "string" ? s : (s?.text || s?.message || "");
      if (text) out.push({ cpt: c.code, text });
    }));
    return out;
  }, [note]);

  const unpointed = lines.map((l, i) => ({ l, i })).filter(({ i }) => isUnlinked(i));
  const noAvg = lines.filter((l) => !lineCharge(l));
  const blockers = [];
  lines.forEach((l, i) => { const u = lineUnits(l); if (!(u >= 1) || !Number.isInteger(u)) blockers.push({ key: `u${i}`, sel: { type: "line", i }, t: `Line ${i + 1} — ${l.code} units must be a whole number of 1 or more`, s: "Box 24G can't be blank or zero. Fix the units on this line." }); });
  unpointed.forEach(({ i, l }) => blockers.push({ key: `p${i}`, sel: { type: "line", i }, t: `Line ${i + 1} — ${l.code} has no diagnosis pointer`, s: "With nothing in Box 24E this claim cannot be saved. Point the line at a diagnosis; any version already saved is unaffected." }));
  if (!patientMatched) blockers.push({ key: "pt", sel: { type: "patient" }, t: "Patient is not matched to a roster record", s: "Resolve the match on the patient record before this goes out." });
  if (!payerName) blockers.push({ key: "py", sel: { type: "patient" }, t: "No payer on the claim", s: "A claim with no payer cannot be adjudicated. Add the payer in Patient & payer." });
  const pointerBlockers = unpointed.map(({ i, l }) => ({ key: `p${i}`, i, code: l.code, desc: l.description || "" }));
  const otherBlockers = [];
  lines.forEach((l, i) => { const u = lineUnits(l); if (!(u >= 1) || !Number.isInteger(u)) otherBlockers.push({ key: `u${i}`, t: `Line ${i + 1} — ${l.code} units must be 1 or more`, s: "Box 24G can't be blank or zero.", act: "Go to line →", sel: { type: "line", i } }); });
  if (!patientMatched) otherBlockers.push({ key: "pt", t: "Patient is not matched to a roster record", s: "No MRN resolved, so the patient record cannot be confirmed.", act: "Open patient →", sel: { type: "patient" } });
  if (!payerName) otherBlockers.push({ key: "py", t: "No payer on the claim", s: "A claim with no payer cannot be adjudicated. Add the payer in Patient & payer.", act: "Open payer →", sel: { type: "payer" } });
  const reviewCount = observations.length + noAvg.length;
  const word = blockers.length ? "Check" : reviewCount ? "Review" : "Clear";
  const wordColor = blockers.length ? "#C4302A" : reviewCount ? "#9A5A00" : INK;
  const charges = lines.map(lineCharge);
  const total = charges.reduce((s, c) => s + (c ? c.amt : 0), 0);
  const anyCharge = charges.some(Boolean), totalEst = charges.some((c) => c?.est);
  const fin = note?.financial_summary || null;
  const linesPointed = lines.filter((l) => (l.dx || []).length).length;
  const dos = claim.date_of_service || note?.date_of_service || null;
  const posCode = claim.facility?.pos_code || note?.pos_code || null;
  const posDesc = claim.facility?.pos_desc || note?.place_of_service?.description || "";
  const provName = claim.rendering_provider?.name || note?.provider_name || "";
  const provNpi = claim.rendering_provider?.npi || note?.npi_number || "";
  const rr = useProviderRules(provNpi);
  const claimCpts = lines.map((l) => ({ code: l.code, description: l.description || "" })).filter((c) => c.code);
  const claimIcds = dx.map((d) => ({ code: d.code, description: d.description || "" })).filter((d) => d.code);
  const goManageRules = () => window.open("/settings", "_blank", "noopener");
  const provRuleCount = rr.inList ? rr.inc.length + rr.exc.length : null;
  const renderingSub = !provNpi ? <Tok /> : !rr.inList ? "Not in Practice Setup" : provRuleCount ? `NPI ${provNpi} · ${provRuleCount} pre-claim rule${provRuleCount === 1 ? "" : "s"}` : `NPI ${provNpi} · No pre-claim rules`;
  const [rulePrefill, setRulePrefill] = useState(null);
  const [rulesSheet, setRulesSheet] = useState(false);
  const lastAttempt = useRef(null);
  useEffect(() => { if (rr.changed) setRulesSheet(true); }, [rr.changed]);
  const recordAttempt = (a) => { lastAttempt.current = a; };
  const openRulePrefill = (code, kind) => {
    const text = kind === "never" ? `Never bill ${code} ` : `Always ${code} `;
    setRulePrefill({ text, kind, locked: true, nonce: Date.now() });
    setRailTab("verdict"); setSel({ type: "verdict" });
    setTimeout(() => document.querySelector('[data-testid="cfv-rules-verdict-head"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
  };
  const putRuleBack = () => {
    const a = lastAttempt.current;
    if (a) { setRulePrefill({ text: a.text, kind: a.kind, locked: false, nonce: Date.now() }); setRailTab("verdict"); setSel({ type: "verdict" }); }
    setRulesSheet(false);
  };
  const FREQ = { "1": "Original", "7": "Replacement", "8": "Void / cancel" };
  const freq = claim.payer?.claim_frequency || "1";
  // pointer-slot breakdown for a diagnosis: how many lines carry it in 24E slot 1..4
  const slotCounts = (code) => [0, 1, 2, 3].map((k) => lines.filter((l) => (l.dx || [])[k]?.code === code).length);

  // claim_readiness is coding-only; read its status VERBATIM (form no longer computes its own word).
  const cr = note?.claim_readiness || {};
  const codingLabel = ({ ready: "Ready", review: "Needs review", needs_review: "Needs review", warnings: "Ready with warnings", blocked: "Blocked" })[String(cr.status)]
    || (cr.status ? String(cr.status).charAt(0).toUpperCase() + String(cr.status).slice(1) : null);
  const cd = claim.claim_details || {};
  const setCD = (k, v) => mutate((c) => { c.claim_details = { ...(c.claim_details || {}), [k]: v }; });


  const ptrDrag = useRef(null); // { line, from } for chip reorder within a line
  const boxDragCode = useRef(null); // Box 21 code being dragged toward a Dx cell
  const touch = (i) => setTouchedLines((s) => { const n = new Set(s); n.add(i); return n; });
  const visitDos = note?.date_of_service || claim.date_of_service || null;
  const addCpt = (r) => {
    const first = (claim.diagnoses || [])[0];
    const i = (claim.service_lines || []).length;
    mutate((c) => c.service_lines.push({
      code: String(r.code), description: r.description || "", dos_from: visitDos, dos_to: visitDos,
      units: 1, billed_charge: { units: 1 }, modifiers: [], source: "added", avg_paid: null, evidence: "", rationale: "",
      dx: first ? [{ code: first.code, description: first.description || "" }] : [],
    }));
    touch(i); setSel({ type: "line", i });
    toast.success(`${r.code} added as line ${i + 1}${first ? ` · pointed at ${first.code}` : ""}`);
  };
  const [cptEdit, setCptEdit] = useState(null);
  const replaceCpt = (i, r) => {
    const old = lines[i]?.code;
    if (String(r.code) === String(old)) return;
    mutate((c) => {
      const l = c.service_lines[i];
      if (l.source !== "added" && !l.replaced_from) l.replaced_from = l.code;
      l.code = String(r.code); l.description = r.description || ""; l.avg_paid = null; l.edited = true;
    });
    touch(i); setSel({ type: "line", i });
    toast.success(`Line ${i + 1} changed from ${old} to ${r.code}`);
  };
  const removeLine = (i) => { mutate((c) => { c.service_lines.splice(i, 1); }); setTouchedLines(new Set()); setSel({ type: "verdict" }); };
  const setLineDos = (i, v) => { mutate((c) => { c.service_lines[i].dos_from = v || null; c.service_lines[i].dos_to = v || null; }); touch(i); };
  const otherDos = lines.filter((l) => l.dos_from && visitDos && String(l.dos_from).slice(0, 10) !== String(visitDos).slice(0, 10)).length;
  const headKey = [longDate(visitDos || dos), posCode, provName, provNpi, otherDos, hasEncounter].join("|");
  useEffect(() => { cw?.setHead({ dos: longDate(visitDos || dos), pos: posCode, prov: nameCase(provName), npi: provNpi, otherDos, dictation: hasEncounter }); }, [headKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!cw?.cmd) return;
    if (cw.cmd.type === "source") setShowSource(true);
    if (cw.cmd.type === "note" && hasEncounter) { setRailTab("note"); setNoteJump(null); }
    if (cw.cmd.type === "payer") {
      setRailTab("verdict"); setSel({ type: "payer" });
      setTimeout(() => { const r = document.querySelector('[data-testid="cfv-rail"]'); r?.scrollTo?.({ top: 0 }); r?.scrollIntoView?.({ behavior: "smooth", block: "nearest" }); }, 60);
    }
  }, [cw?.cmd?.n]); // eslint-disable-line react-hooks/exhaustive-deps
  const addPtr = (i, code) => { mutate((c) => { const arr = c.service_lines[i].dx = c.service_lines[i].dx || []; if (arr.length < 4 && !arr.some((x) => x.code === code)) arr.push({ code, description: (dx.find((d) => d.code === code) || {}).description || "" }); }); touch(i); };
  const removePtr = (i, code) => { mutate((c) => { c.service_lines[i].dx = (c.service_lines[i].dx || []).filter((x) => x.code !== code); }); touch(i); };
  const reorderPtr = (i, from, to) => { if (from === to || from == null) return; mutate((c) => { const arr = c.service_lines[i].dx; const [m] = arr.splice(from, 1); arr.splice(to, 0, m); }); touch(i); setReordered(true); };
  const setUnits = (i, raw) => {
    const n = raw === "" ? null : Number(raw);
    mutate((c) => {
      const l = c.service_lines[i], cur = lineCharge(l), oldU = lineUnits(l) || 1;
      const rate = l.billed_charge?.unit ?? (cur ? cur.amt / oldU : null);
      l.units = n;
      l.billed_charge = { ...(l.billed_charge || {}), units: n, ...(rate != null && n >= 1 ? { unit: round2(rate), total: round2(rate * n) } : {}), source: "manual" };
    });
    touch(i);
  };
  const setCharge = (i, raw) => {
    const v = String(raw).replace(/[$,\s]/g, ""), amt = v === "" ? null : Number(v);
    if (amt != null && (isNaN(amt) || amt < 0)) { toast.error("Enter a charge like 125.00"); return; }
    mutate((c) => {
      const l = c.service_lines[i], u = lineUnits(l) || 1;
      l.billed_charge = { ...(l.billed_charge || {}), units: u, total: amt == null ? null : round2(amt), unit: amt == null ? null : round2(amt / u), source: "manual" };
    });
    touch(i);
  };

  const save = async () => {
    const boxErrs = clbRef.current?.getErrors?.() || [];
    if (boxErrs.length) { setBoxesOpen(true); setTimeout(() => clbRef.current?.focusFirstError?.(), 30); return; }
    setSaving(true);
    try {
      let n = await saveClaim(note.id, claim);
      if (reordered) {
        // Box 21 was reordered: the first save persists the new diagnosis order but leaves the
        // Box 24E pointers resolved against the OLD order. Re-sending the same snapshot re-resolves
        // the pointers against the now-persisted order. (Decision 1a — two saves behind one press.)
        n = (await saveClaim(note.id, claim)) || n;
        setReordered(false);
      }
      if (n) { setNote(n); setClaim(composeClaim(n)); }
      setDirty(false); toast.success("Claim saved"); onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't save the claim");
    } finally { setSaving(false); }
  };



  const onDrop = (to) => {
    const from = dragIdx.current; dragIdx.current = null;
    if (from == null || from === to) return;
    mutate((c) => { const arr = c.diagnoses; const [m] = arr.splice(from, 1); arr.splice(to, 0, m); });
    setReordered(true);
  };

  const copyCode = (code) => { navigator.clipboard?.writeText(code); toast.success(`Copied ${code}`); };

  // Six 837 loop tiles (3 across · 2 rows). The verdict is NOT a tile — it owns the rail by default.
  // Only Patient/subscriber and Payer may flag RED; setup gaps show amber `needs setup` and never block a save.
  const tiles = [
    { id: "patient", loop: "2010BA/CA", label: "Patient / subscriber", value: patient || <Tok />, red: !patientMatched, sub: patientMatched ? "matched to roster" : "not matched to roster" },
    { id: "rendering", loop: "NM1*82 · 2310B", label: "Rendering provider", value: provName ? nameCase(provName) : <Tok />, sub: renderingSub },
    { id: "billing", loop: "NM1*85 · 2010AA", label: "Billing provider", value: claim.billing_provider?.name ? nameCase(claim.billing_provider.name) : "No billing provider", sub: claim.billing_provider?.npi ? `NPI ${claim.billing_provider.npi}` : "NPI & Tax ID come from setup" },
    { id: "facility", loop: "2310C", label: "Service facility", value: claim.facility?.name ? nameCase(claim.facility.name) : "No facility on file", sub: posCode ? `POS ${posCode}${posDesc ? ` · ${posDesc}` : ""}` : <Tok setup /> },
    { id: "payer", loop: "2010BB", label: "Payer", value: payerName || <Tok />, red: !payerName, sub: claim.payer?.payer_id ? `ID ${claim.payer.payer_id}` : <Tok /> },
    { id: "referring", loop: "NM1*DN · 2310A · optional", label: "Referring physician", value: claim.referring_provider?.name || "none", sub: claim.referring_provider?.npi ? `NPI ${claim.referring_provider.npi}` : "optional" },
  ];

  return (
    <div className="cfv" data-testid="claimform-v20">
      <div className="rvq-leave-veil" data-on={leavingToChart} aria-hidden="true" />
      {/* black stats bar + claim heading — only outside the workspace (its header carries these) */}
      {!cw ? (<>
      <div className="cfv-topbar" data-testid="cfv-stats">
        <span><b>{lines.length}</b> CPT{lines.length === 1 ? "" : "s"} coded</span>
        <span><b>{dx.length}</b> diagnos{dx.length === 1 ? "is" : "es"}</span>
        <span><b>{linesPointed}</b> line{linesPointed === 1 ? "" : "s"} pointed</span>
        {unpointed.length ? <span className="cfv-stat-warn"><b>{unpointed.length}</b> need{unpointed.length === 1 ? "s" : ""} a pointer</span> : null}
      </div>

      {/* claim heading */}
      <div className="cfv-claimhead">
        <div className="cfv-ch-icon"><FileText size={18} /></div>
        <div className="cfv-ch-txt" data-testid="cfv-ident">
          <div className="cfv-ch-eyebrow">Claim</div>
          <div className="cfv-ch-name">{patient || "—"}</div>
          <div className="cfv-ch-meta">
            <span data-testid="cfv-visit-dos">Visit date {longDate(visitDos || dos)}</span>
            {otherDos ? <span style={{ color: "#9A5A00", fontWeight: 600 }} data-testid="cfv-other-dos"> · {otherDos} line{otherDos === 1 ? "" : "s"} on another date</span> : null}
            {` · POS ${posCode || "—"}`}{provName ? ` · ${nameCase(provName)}` : ""}{provNpi ? ` · NPI ${provNpi}` : ""}
            {hasEncounter ? (
              <> · <button className="cfv-ch-dict" data-testid="cfv-dictation-chip" title="Open the encounter note" onClick={() => { setRailTab("note"); setNoteJump(null); }}>{/^dict/i.test(note?.visit_source || "") ? "From a dictation session" : /doc|upload/i.test(note?.visit_source || "") ? "From an uploaded document" : /tele/i.test(note?.visit_source || "") ? "From a televisit" : "Has an encounter note"}</button></>
            ) : null}
          </div>
        </div>
        <button className="cfv-source" data-testid="cfv-view-source" onClick={() => setShowSource(true)}><FileText size={13} /> View source · evidence</button>
      </div>
      </>) : null}

      {showSource ? <SourceEvidence note={note} onBack={() => setShowSource(false)} /> : null}
      {cptEdit ? <CptPicker cid={note?.client_id || selectedClientId} anchor={cptEdit.anchor} title={`Change line ${cptEdit.i + 1} · ${lines[cptEdit.i]?.code || ""} → pick from the clinic CPT list`} testid="cfv-changecpt"
        onClaim={(code) => lines.some((l) => String(l.code) === String(code))} onClose={() => setCptEdit(null)} onPick={(r) => { replaceCpt(cptEdit.i, r); setCptEdit(null); }} /> : null}

      <div className="cfv-body" ref={rail.bodyRef} style={{ "--railw": `${rail.width}px` }} data-dragging={rail.dragging ? "1" : undefined}>
        <RailHandle rail={rail} />
        {/* MAIN — tiles + Box 21 + Box 24 (capped width) */}
        <div className="cfv-main">
          {sel.type !== "verdict" ? (
            <button className="cfv-standing" data-testid="cfv-standing-verdict" onClick={() => setSel({ type: "verdict" })}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: 0, padding: 0, marginBottom: 12, fontSize: 12, color: "#6E6E6B", cursor: "pointer" }}>
              <Emblem size={12} />
              <span style={{ borderBottom: "1px solid #D8D8DC", paddingBottom: 1 }}>back to verdict</span>
            </button>
          ) : null}
          <div className="cfv-tiles" data-testid="cfv-tiles" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 10 }}>
            {tiles.map((t) => (
              <button key={t.id} data-testid={`cfv-tile-${t.id}`}
                onClick={() => { setRailTab("verdict"); setSel((s) => (s.type === t.id ? { type: "verdict" } : { type: t.id })); }}
                className={`cfv-tile ${sel.type === t.id ? "cfv-tile-on" : ""}`}
                style={t.red ? { borderColor: "#E8C4C1", background: "#FDF4F3" } : undefined}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 6 }}>
                  <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".02em", textTransform: "uppercase", color: t.red ? "#C4302A" : "#6E6E6B" }}>{t.label}</span>
                  <span style={{ fontSize: 8.5, letterSpacing: ".03em", color: "#C4C4C8", fontFamily: "ui-monospace, SFMono-Regular, monospace", whiteSpace: "nowrap" }}>{t.loop}</span>
                </div>
                <div className="cfv-tile-val" title={typeof t.value === "string" ? t.value : undefined} style={{ color: t.red ? "#C4302A" : INK }}>{t.value}</div>
                <div className="cfv-tile-sub">{t.sub}</div>
              </button>
            ))}
          </div>
          {/* Claim-level boxes — redesigned, collapsible, sits above the diagnoses */}
          <ClaimLevelBoxes
            ref={clbRef}
            open={boxesOpen}
            onToggle={() => setBoxesOpen((v) => !v)}
            resetKey={note?.id}
            claimFrequency={claim.payer?.claim_frequency || "1"}
            onFreq={(v) => mutate((c) => { c.payer = { ...c.payer, claim_frequency: v }; })}
            priorAuth={claim.prior_auth_number || ""}
            onPriorAuth={(v) => mutate((c) => { c.prior_auth_number = v || null; })}
            dos={visitDos || lines[0]?.dos_from || ""}
            cd={cd}
            setCD={setCD}
          />
          <div className="cfv-sec-head">
            <span className="cfv-sec-t" style={{ color: INK }}>Diagnoses <span className="cfv-box">Box 21 · 1–12</span></span>
            <span className="cfv-sec-note" data-testid="cfv-dx-count">{dxAddedCount > 0 ? `${dx.length} of 12 · ${dxNoteCount} resolved from the note · ${dxAddedCount} added` : `${dx.length} of 12 · resolved from the note`}</span>
          </div>
          <div className="cfv-dx-row" data-testid="cfv-dx-chips">
            {dx.map((d, i) => {
              const unused = !usedCodes.has(d.code); const n = linesForDx(d.code); const typed = dxTyped(d);
              const slots = slotCounts(d.code);
              return (
                <div key={d.code} className="cfv-dxc" draggable data-testid={`cfv-dx-${d.code}`}
                  onClick={() => setSel({ type: "dx", code: d.code })}
                  onDragStart={() => { dragIdx.current = i; boxDragCode.current = d.code; }} onDragEnd={() => { boxDragCode.current = null; }} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
                  style={{ border: "1px solid #E9E9EC", background: "#fff", cursor: "pointer" }}>
                  <GripVertical size={12} className="cfv-dxc-grip" />
                  <span className="cfv-dxc-n" style={{ background: INK }}>{i + 1}</span>
                  <span className="cfv-dxc-code" style={{ color: INK }}>{d.code}</span>
                  {typed ? <span className="cfv-dxc-mark" title="typed on this claim — no description">typed</span>
                    : dxAdded(d) ? <span className="cfv-dxc-added" title="added on this claim — revQ resolved the rest">added</span>
                    : dxInferred(d) ? <span className="cfv-dxc-i"><Info size={11} /></span> : null}
                  <span className="cfv-dxc-lines" style={{ color: unused ? "#9A5A00" : "#6E6E6B" }}>{unused ? "unused" : `${n} line${n === 1 ? "" : "s"}`}</span>
                  <button className="cfv-dxc-x" data-testid={`cfv-dx-remove-${d.code}`} aria-label={`Remove ${d.code}`}
                    onClick={(e) => { e.stopPropagation(); mutate((c) => { c.diagnoses = c.diagnoses.filter((x) => x.code !== d.code); c.service_lines.forEach((l) => { l.dx = (l.dx || []).filter((x) => x.code !== d.code); }); }); }}>×</button>
                  <div className="cfv-dxc-tip">
                    <b>{d.code}</b>{d.description ? ` — ${d.description}` : typed ? " — typed on this claim, no description" : ""}
                    <span className="cfv-dxc-tip-slots">Pointer slots: DX1 {slots[0]} · DX2 {slots[1]} · DX3 {slots[2]} · DX4 {slots[3]}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <AddDiagnosis dx={dx} note={note} onAdd={addDx} />

          <div className="cfv-sec-head" style={{ marginTop: 18 }}>
            <span className="cfv-sec-t" style={{ color: INK }}>Service lines <span className="cfv-box">Box 24</span></span>
            <span className="cfv-sec-note inline-flex items-center gap-2"><span data-testid="cfv-lines-count">{lines.length} line{lines.length === 1 ? "" : "s"}{cw ? ` · ${linesPointed} pointed` : ""}{cw && unpointed.length ? <span style={{ color: "#9A5A00", fontWeight: 600 }}> · {unpointed.length} need{unpointed.length === 1 ? "s" : ""} a pointer</span> : null}</span>
              <AddCptLine cid={note?.client_id || selectedClientId} onAdd={addCpt} onClaim={(code) => lines.some((l) => String(l.code) === String(code))} /></span>
          </div>
          <div className="cfv-table2" data-testid="cfv-service-lines">
            <table className="cfv-tbl">
              <colgroup>
                <col style={{ width: 34 }} /><col style={{ width: 132 }} />
                <col /><col style={{ width: 96 }} /><col style={{ width: 206 }} /><col style={{ width: 62 }} /><col style={{ width: 112 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Line</th><th>DOS 24A</th><th>Procedure · CPT</th><th>Mods</th><th>Dx pointer 24E</th><th className="cfv-r" title="Box 24G">Units</th><th className="cfv-r">Charge 24F</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => {
                  const unlinked = isUnlinked(i); const ch = charges[i]; const un = lineUnits(l);
                  const pts = l.dx || []; const human = touchedLines.has(i);
                  return (
                    <React.Fragment key={i}>
                    <tr data-testid={`cfv-line-${i}`} onClick={() => setSel({ type: "line", i })}
                      className={`cfv-trow ${sel.type === "line" && sel.i === i ? "cfv-trow-on" : ""}`} style={unlinked ? { background: "#FDEDEC" } : undefined}>
                      <td style={{ fontWeight: 700, color: unlinked ? "#C4302A" : "#9B9B97" }}>{i + 1}</td>
                      <td className="cfv-nowrap" onClick={(e) => e.stopPropagation()}>
                        <input type="date" className="cfv-numin" style={{ width: 122, textAlign: "left", color: visitDos && l.dos_from && String(l.dos_from).slice(0, 10) !== String(visitDos).slice(0, 10) ? "#9A5A00" : INK }}
                          value={String(l.dos_from || visitDos || "").slice(0, 10)} onChange={(e) => setLineDos(i, e.target.value)}
                          aria-label={`Date of service for line ${i + 1}`} data-testid={`cfv-dos-${i}`} />
                      </td>
                      <td>
                        <div className="cfv-cptcode inline-flex items-center gap-1.5" style={{ color: INK }}>{l.code}
                          {l.source === "added" ? <span className="text-[11px]" style={{ color: "#6E6E6B", fontFamily: "inherit", fontWeight: 400 }} data-testid={`cfv-line-added-${i}`}>Added</span> : null}
                          <button type="button" className="cfv-qbtn" onClick={(e) => { e.stopPropagation(); setCptEdit({ i, anchor: e.currentTarget }); }} data-testid={`cfv-change-cpt-${i}`} title="Change this CPT">Change</button>
                          {l.source === "added" ? <button type="button" className="cfv-qbtn" onClick={(e) => { e.stopPropagation(); removeLine(i); }} data-testid={`cfv-remove-line-${i}`} title="Remove this added line">Remove</button> : null}
                          {l.replaced_from ? <span className="text-[9.5px] font-semibold" style={{ color: "#6E6E6B" }} title={`revQ coded ${l.replaced_from}`}>was {l.replaced_from}</span> : null}
                        </div>
                        <div className="cfv-desc" title={l.description}>{l.description}</div>
                      </td>
                      <td className="cfv-nowrap">
                        {(l.modifiers || []).length
                          ? <span className="cfv-modset">{l.modifiers.map((m) => <span key={m} className="cfv-modchip">{m}</span>)}<button className="cfv-mini" data-testid={`cfv-setmod-${i}`} onClick={(e) => { e.stopPropagation(); setPop({ kind: "mod", i }); }}>Edit</button></span>
                          : <button className="cfv-mini" data-testid={`cfv-setmod-${i}`} onClick={(e) => { e.stopPropagation(); setPop({ kind: "mod", i }); }}>+ mod</button>}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="cfv-ptrcell" data-testid={`cfv-ptrcell-${i}`}
                          onDragOver={(e) => { if (boxDragCode.current) e.preventDefault(); }}
                          onDrop={(e) => { if (boxDragCode.current) { e.preventDefault(); addPtr(i, boxDragCode.current); boxDragCode.current = null; } }}>
                          {pts.map((x, k) => (
                            <span key={x.code} className="cfv-ptrchip" data-testid={`cfv-ptr-${i}-${x.code}`} draggable
                              onDragStart={() => { ptrDrag.current = { line: i, from: k }; }}
                              onDragOver={(e) => { if (ptrDrag.current?.line === i) e.preventDefault(); }}
                              onDrop={(e) => { if (ptrDrag.current?.line === i) { e.preventDefault(); reorderPtr(i, ptrDrag.current.from, k); ptrDrag.current = null; } }}
                              title={x.description || ""}>
                              <GripVertical size={11} className="cfv-ptrchip-grip" />
                              <b className="cfv-ptrchip-badge">{numOf(x.code)}</b>
                              <span className="cfv-ptrchip-code">{x.code}</span>
                              {k === 0 ? <span className="cfv-ptrchip-primary">PRIMARY</span> : null}
                              <button className="cfv-ptrchip-x" data-testid={`cfv-ptr-remove-${i}-${x.code}`} aria-label={`Remove ${x.code}`} onClick={(e) => { e.stopPropagation(); removePtr(i, x.code); }}>×</button>
                            </span>
                          ))}
                          {unlinked && !pts.length ? <span className="cfv-nodx">no Dx</span> : null}
                          {pts.length < 4
                            ? <button className="cfv-ptr-add" data-testid={`cfv-ptr-add-${i}`} onClick={(e) => { e.stopPropagation(); setPtrMenu((m) => (m === i ? null : i)); }}>+</button>
                            : <span className="cfv-ptr-max" data-testid={`cfv-ptr-max-${i}`}>4 is the maximum</span>}
                          {ptrMenu === i && pts.length < 4 ? (
                            <PointerMenu ranked={menuRanked(i)} onPick={(code) => addPtr(i, code)} onClose={() => setPtrMenu(null)} testid={`cfv-ptr-menu-${i}`} anchorTestid={`cfv-ptr-add-${i}`} />
                          ) : null}
                        </div>
                      </td>
                      <td className="cfv-r" onClick={(e) => e.stopPropagation()}>
                        <input key={`u-${i}-${un}`} type="number" min={1} step={1} defaultValue={un ?? ""} className="cfv-numin" style={{ width: 44, borderColor: un >= 1 ? "#E3E3E6" : "#C4302A" }}
                          onBlur={(e) => { if (String(e.target.value) !== String(un ?? "")) setUnits(i, e.target.value); }} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                          aria-label={`Units for line ${i + 1}`} data-testid={`cfv-units-${i}`} />
                      </td>
                      <td className="cfv-r" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-0.5"><span style={{ color: "#6E6E6B" }}>$</span>
                          <input key={`c-${i}-${ch ? ch.amt : "x"}`} inputMode="decimal" defaultValue={ch ? Number(ch.amt).toFixed(2) : ""} placeholder="—" className="cfv-numin" style={{ width: 74, color: INK, fontWeight: 600, ...(!ch ? { borderColor: "#E0A030" } : {}) }}
                            onBlur={(e) => { const v = e.target.value.trim(); if (v !== (ch ? Number(ch.amt).toFixed(2) : "")) setCharge(i, v); }} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                            aria-label={`Charge for line ${i + 1}`} data-testid={`cfv-charge-${i}`} /></div>
                        {ch?.est ? <div className="text-[11px] mt-0.5" style={{ color: "#6E6E6B" }} title="No rate on file. This is the corpus average paid for this CPT; type a charge to replace it." data-testid={`cfv-charge-est-${i}`}>Estimated · corpus avg</div>
                          : !ch ? <div className="text-[9.5px] mt-0.5" style={{ color: "#9A5A00" }} data-testid={`cfv-charge-none-${i}`}>needs a rate</div>
                          : l.billed_charge?.source ? <div className="text-[9.5px] mt-0.5" style={{ color: "#6E6E6B" }} data-testid={`cfv-charge-src-${i}`}>{l.billed_charge.source === "manual" ? "entered" : String(l.billed_charge.source).replace(/_/g, " ")}{un > 1 && l.billed_charge.unit != null ? ` · ${money(l.billed_charge.unit)}/unit` : ""}</div> : null}
                      </td>
                    </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={6} className="cfv-foot-note" data-testid="cfv-total-note">Box 28 · {lines.length} line{lines.length === 1 ? "" : "s"} · total of line charges{totalEst ? " · est = includes corpus average where no rate is on file" : ""}{noAvg.length ? ` · ${noAvg.length} line${noAvg.length === 1 ? "" : "s"} with no charge` : ""}</td>
                  <td className="cfv-r" style={{ fontWeight: 800, color: INK }} data-testid="cfv-total">{anyCharge ? money(total) : "—"}{anyCharge && totalEst ? <div className="text-[11px] font-normal" style={{ color: "#6E6E6B" }}>Estimated</div> : null}</td>
                </tr>
                {fin ? (
                  <tr data-testid="cfv-financial-summary">
                    <td colSpan={7} className="cfv-foot-note">
                      Superbill · charges {money(fin.total_charges) || "—"} · insurance paid {money(fin.insurance_paid) || "—"} · adjustments {money(fin.adjustments) || "—"} · patient {money(fin.patient_responsibility) || "—"} · balance <b>{money(fin.balance) || "—"}</b>
                    </td>
                  </tr>
                ) : null}
              </tfoot>
            </table>
          </div>
        </div>

        {/* RAIL — single slot, driven by selection */}
        <div className="cfv-rail" data-testid="cfv-rail">
          <div className="cfv-railtabs" role="tablist" data-testid="cfv-rail-tabs">
            <button role="tab" aria-selected={railTab === "verdict"} className={railTab === "verdict" ? "on" : ""} data-testid="cfv-tab-verdict" onClick={() => setRailTab("verdict")}>Claim</button>
            <button role="tab" aria-selected={railTab === "note"} className={railTab === "note" ? "on" : ""} data-testid="cfv-tab-note"
              disabled={!hasEncounter} title={hasEncounter ? undefined : "No encounter note on this visit yet"}
              onClick={() => hasEncounter && setRailTab("note")}>
              Encounter note{noteEditing ? <span className="cnt" data-testid="cfv-tab-note-editing">editing</span> : null}
            </button>
          </div>
          {railTab === "note" && hasEncounter ? (
            <EncounterNote note={note} onOpenInChart={openInChart} jump={noteJump} onEditing={setNoteEditing} />
          ) : sel.type === "verdict" ? (
            <>
              <div className="cfv-rail-eyebrow"><Emblem size={13} /> Claim</div>
              <div data-testid="cfv-coding-verdict" style={{ fontSize: 14, fontWeight: 800, color: INK, margin: "10px 0 1px" }}>{codingLabel || "No coding verdict returned"}</div>
              <div className="cfv-rail-line">{typeof cr.blockers === "number" ? `${cr.blockers} flagged in coding` : "coding counts not returned"}{typeof cr.needs_review === "number" ? ` · ${cr.needs_review} to review` : ""}</div>
              {rr.inList ? <div className="cfv-rail-line" data-testid="cfv-verdict-rulecount">{provRuleCount} pre-claim rule{provRuleCount === 1 ? "" : "s"} for {titleCase(provName)}</div> : null}
              {blockers.length ? <div className="cfv-rail-lbl" style={{ color: "#C4302A", marginTop: 16 }} data-testid="cfv-mustfix">What has to be fixed before it saves <span className="cfv-rail-n" style={{ background: "#FDEDEC", color: "#C4302A" }}>{blockers.length}</span></div> : null}
              {pointerBlockers.length ? (
                <div className="cfv-blocker-note" data-testid="cfv-ptr-blocker-note">{pointerBlockers.length} line{pointerBlockers.length === 1 ? "" : "s"} {pointerBlockers.length === 1 ? "has" : "have"} nothing in Box 24E, so this claim cannot be saved. Any version already saved is unaffected.</div>
              ) : null}
              {pointerBlockers.map((b) => (
                <React.Fragment key={b.key}>
                  <button className="cfv-blocker-row" data-testid={`cfv-blocker-${b.key}`} onClick={() => { setRailTab("verdict"); setSel({ type: "line", i: b.i }); setPtrMenu(b.i); }}>
                    <span className="cfv-blocker-row-l">Line {b.i + 1} · {b.code}<span className="cfv-blocker-row-d">{b.desc}</span></span>
                    <span className="cfv-blocker-row-act">Point it →</span>
                  </button>
                  {hasEncounter ? <button className="cfv-en-readlink" data-testid={`cfv-readnote-${b.key}`} onClick={() => jumpToNote(b.desc || b.code)}>Read what the note says about it →</button> : null}
                </React.Fragment>
              ))}
              {otherBlockers.map((b) => (
                <button key={b.key} className="cfv-blocker" data-testid={`cfv-blocker-${b.key}`} onClick={() => setSel(b.sel)}>
                  <div className="cfv-blocker-t">{b.t}</div><div className="cfv-blocker-s">{b.s}</div>
                  <div className="cfv-blocker-row-act" style={{ marginTop: 4 }}>{b.act}</div>
                </button>
              ))}
              {!blockers.length ? <div className="cfv-empty-line" style={{ marginTop: 16 }} data-testid="cfv-nosave-blockers">Nothing blocks a save.</div> : null}
              <div className="cfv-rail-lbl"><Emblem size={12} /> What revQ noticed {observations.length ? <span className="cfv-rail-n">{observations.length}</span> : null}</div>
              {observations.length ? observations.map((s, i) => (
                <div key={i} className="cfv-sug" data-testid={`cfv-obs-${i}`}>
                  <div className="cfv-sug-top"><b style={{ color: INK }}>{s.cpt}</b> <span className="cfv-sug-tag">observation</span></div>
                  {whyOpen[i] ? <div className="cfv-sug-s">{s.text}</div> : null}
                  <div className="cfv-sug-btns">
                    <button className="cfv-why" data-testid={`cfv-why-${i}`} onClick={() => setWhyOpen((w) => ({ ...w, [i]: !w[i] }))}>{whyOpen[i] ? "Hide" : "Why?"}</button>
                    <button className="cfv-why" data-testid={`cfv-copy-${i}`} onClick={() => copyCode(s.cpt)}><Copy size={11} /> Copy code</button>
                    {hasEncounter ? <button className="cfv-why" data-testid={`cfv-readnote-obs-${i}`} onClick={() => jumpToNote(s.text || s.cpt)}>Read in note →</button> : null}
                  </div>
                </div>
              )) : <div className="cfv-empty-line" data-testid="cfv-noticed-empty">Nothing flagged on these codes.</div>}
              <CodingRulesVerdict rr={rr} provName={provName} cpts={claimCpts} icds={claimIcds}
                onAllRules={() => { setRailTab("verdict"); setSel({ type: "rendering" }); }} onManage={goManageRules}
                prefill={rulePrefill} onAttempt={recordAttempt} />
            </>
          ) : sel.type === "patient" ? (
            <RailEditor title="Patient / subscriber">
              <ReadRow label="Name" value={patient || "Not found"} bad={!patientMatched} sub={patientMatched ? "matched to roster" : "not matched to roster"} />
              <ReadRow label="Date of birth" value={note?.dob || "—"} />
              <ReadRow label="Sex" value={note?.gender || "—"} />
              <ReadRow label="Acct # / MRN" value={note?.mrn || "—"} />
              <ReadRow label="Rel. to subscriber" value={note?.relationship_to_subscriber || "Self"} />
              <div className="cfv-fld"><span className="cfv-fld-l">Address</span><div style={{ marginTop: 2 }}><Tok setup /></div><div style={{ fontSize: 11, color: "#9B9B97", marginTop: 3 }}>from the patient record — cannot be typed here</div></div>
            </RailEditor>
          ) : sel.type === "rendering" ? (
            <RailEditor title="Rendering provider">
              <ReadRow label="Name" value={provName || "—"} />
              <ReadRow label="NPI" value={provNpi || "—"} />
              <ReadRow label="Taxonomy" value={claim.rendering_provider?.taxonomy_code || note?.taxonomy_code || "—"} sub={claim.rendering_provider?.taxonomy_desc || note?.taxonomy_desc || ""} />
              <SourcedSelect label="Switch rendering provider" value={providerOpts.some((o) => o.value === String(provNpi)) ? String(provNpi) : ""} placeholder={provName ? `Keep · ${provName}` : "Select a provider…"} options={providerOpts} emptyMsg="No providers linked to this client yet — add them in Setup" onChange={(v) => pickProvider(v)} testid="cfv-f-switch-rendering" />
              <div style={{ marginTop: 18, borderTop: "1px solid #E9E9EC", paddingTop: 14 }}>
                <CodingRulesProviderPanel rr={rr} provName={provName} cpts={claimCpts} icds={claimIcds} onManage={goManageRules} onAttempt={recordAttempt} />
              </div>
            </RailEditor>
          ) : sel.type === "billing" ? (
            <RailEditor title="Billing provider">
              <SourcedSelect label="Billing provider (organisation / group)" value={billingOpts.some((o) => o.value === String(claim.billing_provider?.npi || claim.billing_provider?.name)) ? String(claim.billing_provider?.npi || claim.billing_provider?.name) : ""} placeholder={claim.billing_provider?.name ? `Keep · ${claim.billing_provider.name}` : "Select a billing provider…"} options={billingOpts} emptyMsg="No billing providers for this client yet — add them in Setup" onChange={pickBilling} testid="cfv-f-billing-select" />
              <ReadRow label="Billing NPI" value={claim.billing_provider?.npi || "—"} />
              <ReadRow label="Tax ID (EIN / SSN)" value={claim.billing_provider?.tax_id || "—"} />
              <div style={{ fontSize: 11, color: "#9B9B97", marginTop: 4 }}>Billing provider, NPI and Tax ID come from the client's billing registry — select one above.</div>
            </RailEditor>
          ) : sel.type === "facility" ? (
            <RailEditor title="Service facility">
              <SourcedSelect label="Service location (facility)" value={facilityOpts.some((o) => o.value === String(claim.facility?.npi || claim.facility?.name)) ? String(claim.facility?.npi || claim.facility?.name) : ""} placeholder={claim.facility?.name ? `Keep · ${claim.facility.name}` : "Select a service location…"} options={facilityOpts} emptyMsg="No service locations for this client yet — add them in Setup" onChange={pickFacility} testid="cfv-f-facility-select" />
              <ReadRow label="Place of service" value={claim.facility?.pos_code || "—"} sub={claim.facility?.pos_desc || ""} />
              <ReadRow label="Facility NPI" value={claim.facility?.npi || "—"} />
            </RailEditor>
          ) : sel.type === "payer" ? (
            <div data-testid="cfv-payer-inspector" style={{ containerType: "inline-size" }}>
              <InsurancePanel note={note} clientId={note?.client_id || selectedClientId}
                onClose={() => setSel({ type: "verdict" })}
                onUpdated={(fresh) => { if (fresh) { setNote(fresh); if (fresh.insurance) setClaim((c) => ({ ...c, payer: { ...c.payer, payer_name: fresh.insurance.payer_name, payer_id: fresh.insurance.payer_id } })); } onChanged?.(); }} />
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #E9E9EC" }}>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "#6E6E6B", marginBottom: 10 }}>Claim boxes</div>
                <div className="cfv-fld"><span className="cfv-fld-l">Date of service</span><div style={{ fontSize: 13, color: "#141412", marginTop: 4 }} data-testid="cfv-f-dos-ro">{lines[0]?.dos_from || note?.date_of_service || "—"}</div><span style={{ fontSize: 10.5, color: "#9B9B97", marginTop: 2 }}>from the service line · the claim header date is not editable (API ignores it)</span></div>
                <SelectField label="Claim frequency (Box 22)" value={claim.payer?.claim_frequency || "1"} onChange={(v) => mutate((c) => { c.payer = { ...c.payer, claim_frequency: v }; })} options={[["1", "1 · Original"], ["7", "7 · Replacement"], ["8", "8 · Void / cancel"]]} testid="cfv-f-freq" />
                <Field label="Prior authorization (Box 23)" value={claim.prior_auth_number || ""} onChange={(v) => mutate((c) => { c.prior_auth_number = v || null; })} placeholder="editable" testid="cfv-f-priorauth" />
              </div>
            </div>
          ) : sel.type === "referring" ? (
            <RailEditor title="Referring physician">
              <SourcedSelect label="Referring physician" value={referringOpts.some((o) => o.value === String(claim.referring_provider?.npi || claim.referring_provider?.name)) ? String(claim.referring_provider?.npi || claim.referring_provider?.name) : ""} placeholder={claim.referring_provider?.name ? `Keep · ${claim.referring_provider.name}` : "Select a referring physician…"} options={referringOpts} emptyMsg="No referring physicians for this client yet — add them in Setup" onChange={pickReferring} testid="cfv-f-referring-select" />
              {claim.referring_provider?.name || claim.referring_provider?.npi ? (
                <button type="button" onClick={clearReferring} data-testid="cfv-f-referring-clear" className="mt-1 inline-flex items-center gap-1 text-[12px] font-semibold text-[#C4302A] hover:underline">
                  <X size={13} /> Clear referring physician
                </button>
              ) : null}
              <ReadRow label="Referring NPI" value={claim.referring_provider?.npi || "—"} />
              <ReadRow label="Specialty" value={claim.referring_provider?.specialty || "—"} />
              <div style={{ fontSize: 11, color: "#9B9B97", marginTop: 4 }}>Referring physician is optional and comes from the client's referring registry. Clearing it removes Box 17 / NM1*DN from the claim on save.</div>
            </RailEditor>
          ) : sel.type === "line" ? (
            <LineInspector line={lines[sel.i]} idx={sel.i} numOf={numOf} onSetPtr={() => setPtrMenu(sel.i)} onSetMod={() => setPop({ kind: "mod", i: sel.i })}
              rr={rr} provName={provName} onRulePrefill={openRulePrefill} onManage={goManageRules} />
          ) : sel.type === "dx" ? (
            <RailEditor title="Diagnosis">
              {(() => { const d = dx.find((x) => x.code === sel.code); return (
                <>
                  <ReadRow label="Code" value={sel.code} />
                  <ReadRow label="Description" value={(d && d.description) || (d && dxTyped(d) ? "typed on this claim — no description" : "—")} />
                  {d && dxAdded(d) ? <ReadRow label="Source" value={`Added on this claim · ICD-10-CM FY${d.added_fy || 2026}`} /> : null}
                  <CodeRulesInspectorBlock code={sel.code} rr={rr} provName={provName} onRulePrefill={openRulePrefill} onManage={goManageRules} />
                </>
              ); })()}
            </RailEditor>
          ) : null}
        </div>
      </div>

      {/* FOOTER — Save claim is the only button */}
      <div className="cfv-foot">
        {onScanNext ? <button className="cfv-foot-ghost" onClick={onScanNext} data-testid="cfv-scan-next">Scan next</button> : null}
        {cw && !note?.signed ? <span className="cfv-foot-block" data-testid="cfv-foot-unsigned"><b style={{ color: "#9A5A00" }}>Not signed</b> · sign the visit to save this claim</span> : null}
        {blockers.length ? <span className="cfv-foot-block" data-testid="cfv-foot-block"><b>{blockers.length} blocking</b> · fix these before this claim goes out</span> : null}
        <span style={{ flex: 1 }} />
        {dirty ? <span className="cfv-unsaved" data-testid="cfv-unsaved">unsaved changes</span> : null}
        <button onClick={save} disabled={saving || (cw && !note?.signed)} data-testid="cfv-save" className="cfv-save">
          {saving ? <Loader2 size={13} className="cfv-spin" /> : null}{saving && reordered ? "Saving reorder…" : <>Save claim{dirty ? " · unsaved changes" : ""}</>}
        </button>
      </div>

      {pop ? <PickerPopover kind={pop.kind} line={lines[pop.i]} lineIdx={pop.i} dx={dx} numOf={numOf} recDx={(note?.cpt_codes?.[pop.i]?.dx || []).map((x) => x.code)}
        onClose={() => setPop(null)}
        onApplyPtr={(codes) => { mutate((c) => { c.service_lines[pop.i].dx = codes.map((cd) => ({ code: cd, description: (dx.find((d) => d.code === cd) || {}).description || "" })); }); setPop(null); }}
        onApplyMod={(mods) => { mutate((c) => { c.service_lines[pop.i].modifiers = mods; }); setPop(null); }} /> : null}
      {rulesSheet ? <RulesChangedSheet canRestore={!!lastAttempt.current} onPutBack={putRuleBack} onClose={() => setRulesSheet(false)} /> : null}
    </div>
  );
}

/* ---------- Add diagnosis (ICD-10-CM search) ---------- */
function AddDiagnosis({ dx, note, onAdd }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null); // {fy, nodes, count, truncated}
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const inputRef = useRef(null);
  const popRef = useRef(null);
  const timerRef = useRef(null);
  const abortRef = useRef(null);
  const lastFyRef = useRef(2026);
  const full = dx.length >= 12;

  const onClaimSet = useMemo(() => new Set(dx.map((d) => normCode(d.code))), [dx]);
  const recommended = useMemo(() => (note?.icd_codes || []).filter((c) => c.code && !onClaimSet.has(normCode(c.code))), [note, onClaimSet]);
  const recSet = useMemo(() => new Set(recommended.map((c) => normCode(c.code))), [recommended]);

  const place = () => {
    const b = btnRef.current; if (!b) return;
    const r = b.getBoundingClientRect();
    const w = Math.min(560, window.innerWidth - 24);
    let left = r.left; if (left + w > window.innerWidth - 12) left = window.innerWidth - 12 - w; if (left < 12) left = 12;
    setPos({ top: r.bottom + 6, left, width: w });
  };
  const close = () => { setOpen(false); setQ(""); setRes(null); setActive(-1); setLoading(false); setTimeout(() => btnRef.current?.focus(), 10); };
  const commit = (node) => { onAdd(node); close(); };

  useEffect(() => { if (!open) return undefined; place(); const h = () => place(); window.addEventListener("resize", h); window.addEventListener("scroll", h, true); return () => { window.removeEventListener("resize", h); window.removeEventListener("scroll", h, true); }; }, [open]);
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 10); }, [open]);
  useEffect(() => { if (!open) return undefined; const h = (e) => { if (popRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return; close(); }; document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h); }, [open]);
  useEffect(() => { if (res?.fy) lastFyRef.current = res.fy; }, [res]);

  useEffect(() => {
    if (!open) return undefined;
    if (timerRef.current) clearTimeout(timerRef.current);
    const term = q.trim();
    if (term.length < 2) { setRes(null); setLoading(false); setActive(-1); return undefined; }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        const { data } = await api.get("/api/edi/note-to-claim/icd/search", { params: { q: term, limit: 50 }, signal: abortRef.current.signal });
        setRes(data); setActive(-1);
      } catch (e) {
        if (e.name !== "CanceledError" && e.code !== "ERR_CANCELED") setRes({ fy: lastFyRef.current, nodes: [], count: 0, truncated: false });
      } finally { setLoading(false); }
    }, 250);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [q, open]);

  const shownFy = res?.fy || lastFyRef.current;
  const nodes = useMemo(() => res?.nodes || [], [res]);
  const navIdx = useMemo(() => nodes.map((n, i) => (n.selectable && !onClaimSet.has(normCode(n.code_dotted))) ? i : -1).filter((i) => i >= 0), [nodes, onClaimSet]);

  const onKey = (e) => {
    if (e.key === "Escape") { e.preventDefault(); close(); return; }
    if (q.trim().length < 2) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault(); if (!navIdx.length) return;
      const cur = navIdx.indexOf(active);
      const ni = e.key === "ArrowDown" ? (cur < 0 ? 0 : Math.min(cur + 1, navIdx.length - 1)) : (cur <= 0 ? 0 : cur - 1);
      setActive(navIdx[ni]);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const n = active >= 0 ? nodes[active] : (navIdx.length ? nodes[navIdx[0]] : null);
      if (n) commit({ code: n.code, code_dotted: n.code_dotted, description: n.description, fy: shownFy });
    }
  };

  if (full) {
    return <button ref={btnRef} className="cfv-adddx-btn" disabled data-testid="cfv-adddx-btn" title="Box 21 holds 12 diagnoses — remove one to add another">12 of 12</button>;
  }

  return (<>
    <button ref={btnRef} className={"cfv-adddx-btn" + (open ? " open" : "")} data-testid="cfv-adddx-btn" aria-expanded={open} onClick={() => (open ? close() : setOpen(true))}>+ Add diagnosis</button>
    {open && pos ? createPortal(
      <div ref={popRef} className="cfv-adddx-pop" data-testid="cfv-adddx-pop" style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width }}>
        <div className="cfv-adddx-search">
          <Search size={15} className="cfv-adddx-mag" />
          <input ref={inputRef} role="combobox" aria-expanded="true" aria-controls="cfv-adddx-list" value={q}
            onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} placeholder="Code or description" data-testid="cfv-adddx-input" />
        </div>
        <div className="cfv-adddx-list" id="cfv-adddx-list" role="listbox">
          {q.trim().length < 2 ? (
            <>
              <div className="cfv-adddx-sec">From the note</div>
              {recommended.length === 0 ? (
                <div className="cfv-adddx-empty">Every code revQ recommended is already on the claim. Type a code or description.</div>
              ) : recommended.map((c) => (
                <div key={c.code} role="option" className="cfv-adddx-opt" data-testid={`cfv-adddx-rec-${c.code}`} onClick={() => commit({ code: c.code, code_dotted: c.code, description: c.description, fy: shownFy })}>
                  <div className="cfv-adddx-optmain">
                    <span className="cfv-adddx-code">{c.code}</span>
                    <span className="cfv-adddx-desc">{c.description || ""}</span>
                    <span className="cfv-adddx-rec">recommended</span>
                  </div>
                  {c.rationale ? <div className="cfv-adddx-rationale">{c.rationale}</div> : null}
                </div>
              ))}
            </>
          ) : loading && !res ? (
            <div className="cfv-adddx-empty">Searching…</div>
          ) : nodes.length === 0 ? (
            <div className="cfv-adddx-empty" data-testid="cfv-adddx-nomatch">No codes match. Try the clinical term or the code.</div>
          ) : (
            <>
              {nodes.map((n, i) => {
                const indent = { paddingLeft: 14 + (n.depth || 0) * 16 };
                if (!n.selectable) {
                  return <div key={n.code + i} className="cfv-adddx-hdr" style={indent}><span className="cfv-adddx-code dim">{n.code_dotted}</span><span className="cfv-adddx-desc">{n.description}</span></div>;
                }
                const onClaim = onClaimSet.has(normCode(n.code_dotted));
                const rec = recSet.has(normCode(n.code_dotted));
                return (
                  <div key={n.code + i} role="option" aria-selected={active === i} aria-disabled={onClaim}
                    className={"cfv-adddx-opt" + (active === i ? " active" : "") + (onClaim ? " disabled" : "")} style={indent}
                    data-testid={`cfv-adddx-opt-${n.code_dotted}`} onMouseEnter={() => !onClaim && setActive(i)}
                    onClick={() => !onClaim && commit({ code: n.code, code_dotted: n.code_dotted, description: n.description, fy: shownFy })}>
                    <div className="cfv-adddx-optmain">
                      <span className="cfv-adddx-code">{n.code_dotted}</span>
                      <span className="cfv-adddx-desc">{n.description}</span>
                      {onClaim ? <span className="cfv-adddx-onclaim">On claim</span> : rec ? <span className="cfv-adddx-rec">recommended</span> : null}
                    </div>
                  </div>
                );
              })}
              {res?.truncated ? <div className="cfv-adddx-foot" data-testid="cfv-adddx-trunc">More codes match — keep typing to narrow.</div> : null}
            </>
          )}
        </div>
      </div>, document.body) : null}
  </>);
}


/* ---------- rail sub-components ---------- */
const RailEditor = ({ title, children }) => (<div data-testid="cfv-rail-editor"><div className="cfv-rail-eyebrow" style={{ color: "#141412" }}>{title}</div><div className="cfv-editor">{children}</div></div>);
const Field = ({ label, value, onChange, placeholder, testid, err }) => (
  <label className="cfv-fld"><span className="cfv-fld-l">{label}</span>
    <input value={value} data-testid={testid} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="cfv-fld-i" style={err ? { borderColor: "#E8C4C1", background: "#FDF4F3" } : undefined} />
    {err ? <span style={{ fontSize: 10.5, color: "#C4302A", marginTop: 3 }} data-testid={`${testid}-err`}>{err}</span> : null}</label>
);
const SelectField = ({ label, value, onChange, options, testid, err }) => (
  <label className="cfv-fld"><span className="cfv-fld-l">{label}</span>
    <select value={value} data-testid={testid} onChange={(e) => onChange(e.target.value)} className="cfv-fld-i" style={err ? { borderColor: "#E8C4C1", background: "#FDF4F3" } : undefined}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
    {err ? <span style={{ fontSize: 10.5, color: "#C4302A", marginTop: 3 }} data-testid={`${testid}-err`}>{err}</span> : null}</label>
);
const DateField = ({ label, value, onChange, testid, err, min }) => (
  <label className="cfv-fld"><span className="cfv-fld-l">{label}</span>
    <input type="date" value={value || ""} min={min} data-testid={testid} onChange={(e) => onChange(e.target.value)} className="cfv-fld-i" style={err ? { borderColor: "#E8C4C1", background: "#FDF4F3" } : undefined} />
    {err ? <span style={{ fontSize: 10.5, color: "#C4302A", marginTop: 3 }} data-testid={`${testid}-err`}>{err}</span> : null}</label>
);
const ReadRow = ({ label, value, sub, bad }) => (
  <div className="cfv-fld"><span className="cfv-fld-l">{label}</span>
    <div style={{ fontSize: 14, fontWeight: 700, color: bad ? "#C4302A" : "#141412" }}>{value}</div>
    <div style={{ fontSize: 11, color: bad ? "#C4302A" : "#6E6E6B" }}>{sub}</div></div>
);
const ProvBlock = ({ title, p, onChange, tid, tax }) => (
  <div className="cfv-red-sub">
    <div className="cfv-red-t">{title}</div>
    <Field label="Name" value={p?.name || ""} onChange={(v) => onChange("name", v || null)} placeholder="—" testid={`cfv-f-${tid}-name`} />
    <Field label="NPI" value={p?.npi || ""} onChange={(v) => onChange("npi", v || null)} placeholder="—" testid={`cfv-f-${tid}-npi`} />
    {tax ? <Field label="Tax ID" value={p?.tax_id || ""} onChange={(v) => onChange("tax_id", v || null)} placeholder="—" testid={`cfv-f-${tid}-taxid`} /> : null}
  </div>
);

function LineInspector({ line, idx, numOf, onSetPtr, onSetMod, rr, provName, onRulePrefill, onManage }) {
  if (!line) return null;
  const avg = line.avg_paid?.amt ?? null;
  const ch = lineCharge(line), un = lineUnits(line);
  return (
    <div data-testid={`cfv-line-inspector-${idx}`}>
      <div className="cfv-rail-eyebrow" style={{ color: "#141412" }}>Line {idx + 1}</div>
      <div className="cfv-li-code">{line.code}</div>
      <div className="cfv-li-desc">{line.description}</div>
      <div className="cfv-li-grid">
        <div><span className="cfv-li-k">Units · Box 24G</span><span className="cfv-li-v" data-testid={`cfv-li-units-${idx}`}>{un ?? "—"}</span></div>
        <div><span className="cfv-li-k">Charge · Box 24F</span><span className="cfv-li-v" data-testid={`cfv-li-charge-${idx}`}>{ch ? `${money(ch.amt)}${ch.est ? " (est · corpus average)" : line.billed_charge?.source === "manual" ? " (entered)" : line.billed_charge?.source ? ` (${String(line.billed_charge.source).replace(/_/g, " ")})` : ""}` : "no rate on file"}</span></div>
        {line.billed_charge?.unit != null ? <div><span className="cfv-li-k">Rate per unit</span><span className="cfv-li-v">{money(line.billed_charge.unit)}</span></div> : null}
        <div><span className="cfv-li-k">Corpus average paid</span><span className="cfv-li-v">{avg == null ? "no corpus average" : money(avg)}</span></div>
        <div><span className="cfv-li-k">Modifiers · Box 24D</span><span className="cfv-li-v">{(line.modifiers || []).length ? line.modifiers.map((m) => `${m}${MODIFIER_NAME[m] ? " " + MODIFIER_NAME[m] : ""}`).join(", ") : "none"}</span></div>
      </div>
      <div className="cfv-li-lbl">Linked diagnoses</div>
      {(line.dx || []).length ? (line.dx || []).map((x) => (
        <div key={x.code} className="cfv-li-dx"><b>{numOf(x.code)}</b> {x.code}<span>{x.description || ""}</span></div>
      )) : <div className="cfv-li-none">No diagnosis pointer — this line is blocked.</div>}
      <div className="cfv-li-lbl">Why revQ coded it</div>
      <div className="cfv-li-why">{line.rationale || "—"}{line.evidence ? <div className="cfv-li-ev">“{line.evidence}”</div> : null}</div>
      <div className="cfv-li-btns">
        <button className="cfv-li-btn" data-testid={`cfv-li-setptr-${idx}`} onClick={onSetPtr}>Set pointers</button>
        <button className="cfv-li-btn" data-testid={`cfv-li-setmod-${idx}`} onClick={onSetMod}>Set modifiers</button>
      </div>
      {rr ? <CodeRulesInspectorBlock code={line.code} rr={rr} provName={provName} onRulePrefill={onRulePrefill} onManage={onManage} /> : null}
    </div>
  );
}

/* ---------- honest empty-state token + API-sourced dropdown ---------- */
const Tok = ({ setup }) => setup
  ? <span data-testid="cfv-tok-setup" style={{ fontSize: 11, fontWeight: 700, color: "#9A5A00", background: "#FBEEDD", border: "1px solid #F1E2C4", borderRadius: 5, padding: "1px 6px" }}>needs setup</span>
  : <span style={{ color: "#B8B8BD" }}>—</span>;

const SourcedSelect = ({ label, value, placeholder, options = [], emptyMsg, onChange, testid }) => (
  <label className="cfv-fld"><span className="cfv-fld-l">{label}</span>
    <select className="cfv-fld-i" value={value || ""} data-testid={testid} onChange={(e) => onChange?.(e.target.value)} disabled={!options.length}>
      <option value="">{placeholder || "Select…"}</option>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
    {!options.length ? <span style={{ fontSize: 10.5, color: "#9B9B97", marginTop: 3 }}>{emptyMsg}</span> : null}
  </label>
);

/* ---------- pointer / modifier popover ---------- */
function PickerPopover({ kind, line, lineIdx, dx, numOf, recDx, onClose, onApplyPtr, onApplyMod }) {
  const [ptr, setPtr] = useState(() => new Set((line?.dx || []).map((x) => x.code)));
  const [mods, setMods] = useState(() => [...(line?.modifiers || [])]);
  const [q, setQ] = useState("");
  const toggle = (code) => setPtr((s) => { const n = new Set(s); if (n.has(code)) n.delete(code); else { if (n.size >= 4) return n; n.add(code); } return n; });
  const applyPtr = () => onApplyPtr(dx.filter((d) => ptr.has(d.code)).map((d) => d.code));
  const catalog = MODIFIER_CATALOG.filter(([c, n]) => !q || c.toLowerCase().includes(q.toLowerCase()) || n.toLowerCase().includes(q.toLowerCase()));
  const addMod = (c) => setMods((m) => (m.includes(c) || m.length >= 4 ? m : [...m, c]));
  const rmMod = (c) => setMods((m) => m.filter((x) => x !== c));
  return (
    <div className="cfv-pop-back" onClick={onClose} data-testid="cfv-popover">
      <div className="cfv-pop" onClick={(e) => e.stopPropagation()}>
        {kind === "ptr" ? (
          <>
            <div className="cfv-pop-h">Dx pointers · Line {lineIdx + 1} <span>{ptr.size}/4</span></div>
            <div className="cfv-pop-grid">
              {dx.map((d) => {
                const on = ptr.has(d.code); const rec = recDx.includes(d.code);
                return (
                  <button key={d.code} data-testid={`cfv-pick-${d.code}`} onClick={() => toggle(d.code)} title={d.description || ""}
                    className={`cfv-pick ${on ? "cfv-pick-on" : ""}`}>
                    <b>{numOf(d.code)}</b> {d.code}{rec ? <span className="cfv-pick-rec"><img src="/revQ_emblem_black.webp" alt="revQ" style={{ height: 10 }} /></span> : null}
                  </button>
                );
              })}
            </div>
            <div className="cfv-pop-foot"><span className="cfv-pop-note">four enforced · revQ's pick marked</span><button className="cfv-pop-apply" data-testid="cfv-ptr-apply" onClick={applyPtr}>Apply</button></div>
          </>
        ) : (
          <>
            <div className="cfv-pop-h">Modifiers · Box 24D <span>{mods.length}/4</span></div>
            <div className="cfv-pop-applied">
              {mods.length ? mods.map((m) => <span key={m} className="cfv-mchip">{m} {MODIFIER_NAME[m] || ""}<button onClick={() => rmMod(m)} aria-label="remove">×</button></span>) : <span className="cfv-pop-note">no modifiers</span>}
            </div>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the modifier catalog" className="cfv-pop-search" data-testid="cfv-mod-search" />
            <div className="cfv-pop-list">
              {catalog.map(([c, n]) => (
                <button key={c} className="cfv-mrow" data-testid={`cfv-mod-${c}`} disabled={mods.includes(c) || mods.length >= 4} onClick={() => addMod(c)}>
                  <b>{c}</b> <span>{n}</span>
                </button>
              ))}
            </div>
            <div className="cfv-pop-foot"><span className="cfv-pop-note">four enforced</span><button className="cfv-pop-apply" data-testid="cfv-mod-apply" onClick={() => onApplyMod(mods)}>Apply</button></div>
          </>
        )}
      </div>
    </div>
  );
}


/* ---------- inline Dx-pointer menu (chips are the control · no commit step) ---------- */
function PointerMenu({ ranked, onPick, onClose, testid, anchorTestid }) {
  const popRef = useRef(null);
  const [pos, setPos] = useState(null);
  useEffect(() => {
    const place = () => {
      const a = anchorTestid ? document.querySelector(`[data-testid="${anchorTestid}"]`) : null;
      const pop = popRef.current;
      if (!a || !pop) return;
      const r = a.getBoundingClientRect();
      const ph = pop.offsetHeight || 280;
      const pw = pop.offsetWidth || 290;
      const below = window.innerHeight - r.bottom;
      const openUp = below < ph + 12 && r.top > below; // flip up when the last line has no room below
      let top = openUp ? Math.max(8, r.top - ph - 5) : r.bottom + 5;
      let left = r.left;
      if (left + pw > window.innerWidth - 8) left = window.innerWidth - 8 - pw;
      if (left < 8) left = 8;
      setPos({ top, left });
    };
    place();
    const h = () => place();
    window.addEventListener("resize", h);
    window.addEventListener("scroll", h, true);
    return () => { window.removeEventListener("resize", h); window.removeEventListener("scroll", h, true); };
  }, [anchorTestid, ranked]);
  const Row = (d) => (
    <button key={d.code} type="button" className="cfv-menu-row" data-testid={`cfv-menu-${d.code}`} onClick={() => onPick(d.code)} title={d.description || ""}>
      <b className="cfv-menu-n">{d.n}</b>
      <span className="cfv-menu-code">{d.code}</span>
      <span className="cfv-menu-desc">{d.description || ""}</span>
      {d.cnt > 0 ? <span className="cfv-menu-cnt">{d.cnt} other line{d.cnt === 1 ? "" : "s"}</span> : null}
    </button>
  );
  return createPortal(
    <>
      <div className="cfv-menu-back" onClick={onClose} />
      <div ref={popRef} className="cfv-ptr-menu" data-testid={testid} onClick={(e) => e.stopPropagation()}
        style={{ position: "fixed", top: pos ? pos.top : -9999, left: pos ? pos.left : -9999, visibility: pos ? "visible" : "hidden" }}>
        {ranked.elsewhere.length ? <><div className="cfv-menu-h">revQ pointed these elsewhere</div>{ranked.elsewhere.map(Row)}</> : null}
        {ranked.also.length ? <><div className="cfv-menu-h">also on this claim</div>{ranked.also.map(Row)}</> : null}
        {!ranked.elsewhere.length && !ranked.also.length ? <div className="cfv-menu-empty">Every diagnosis on the claim is already pointed here.</div> : null}
        <div className="cfv-menu-note">revQ has no per-line recommendation — this ranks by what it pointed the other lines at.</div>
      </div>
    </>,
    document.body
  );
}
